import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TabContentWrapper from '../TabContentWrapper';
import styled from 'styled-components';
import {PopoverButton, Glyphs, TscPopoverOrigin, TscButtonType, TscButtonGlyphPlacement, TscStyles, ControlButton, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {IBehaviorFilterEnum, IMemberDetailsModel, ICollectionModel} from '../../../model/appModel';
import AnalyticsSegmentData from './AnalyticsSegmentData';
import cssConstants from '../../../constants/cssConstants';
import UserBehaviorData from './UserBehaviorData';
import Constants, {BehaviorFilters} from '../../../constants/Constants';
import CollectionsMenu from './CollectionsMenu';
import BehaviorFilterMenu from './BehaviorFilterMenu';
import quizApi from '../../../service/quizApi';
import {IWorkerState} from '../../../model/workerState';
import {analyticsDataStore, updateAnalyticsDataFromServer} from '../../../context/analyticsDataProvider';
import {IMediaAnalyticsModel} from '../../../model/mediaAnalyticsModel';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {collectionsStore} from '../../../context/collectionsProvider';
import {mediaStore} from '../../../context/mediaProvider';
import config from '../../../service/config';

const AnalyticsHeader = styled.h2<{theme: TscThemeNames}>`
   font-size: 1.125rem;
   font-weight: 600;
   margin: 0 0.5rem 0 0;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
`;

const AnalyticsHeaderWrapper = styled.div`
   display: flex;
   flex-direction: row;
   align-items: center;
   padding-bottom: .5rem;
`;

const HeaderRightSection = styled.div`
   display: flex;
   align-self: flex-end;
   flex: 0 1 auto;
   min-width: 0rem;

   @media screen and (max-width: ${cssConstants.contentBreakpoint}px) {
      button:nth-child(2) span:nth-child(2) {
         display: none;
      }
   }
`;

const UserBehaviorHeaderWrapper = styled(AnalyticsHeaderWrapper) <{theme: TscThemeNames}>`
   justify-content: space-between;
   border-bottom: 1px solid ${prop => prop.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.dark};
   margin: 1rem 0 .875rem 0;
`;

export const AnalyticsTabBase: React.FC<IWorkerState & IStateMappedProps> = ({isWorking: initialIsWorking, hasError: initialHasError, customErrorMessage: initialCustomErrorMessage, analyticsData, sidebarCollections, CollectionIds, MediaId, Hash, ViewCount}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [state, setState] = useState({
      isWorking: initialIsWorking,
      hasError: initialHasError,
      customErrorMessage: initialCustomErrorMessage,
      selectedCollection: null,
      selectedBehaviorFilter: {selectedFilter: BehaviorFilters.all}
   } as IAnalyticsTabState);

   const updateState = (updatedValues: Partial<IAnalyticsTabState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   useEffect(() => {
      if (state.selectedCollection && CollectionIds.findIndex(hash => hash === state.selectedCollection.Id) === -1) {
         updateState({selectedCollection: null});
      }
   }, [CollectionIds, state.selectedCollection]);

   const getFilteredAnalyticsByCompletion = (analytics: IMediaAnalyticsModel[]): IMediaAnalyticsModel[] =>
      state.selectedBehaviorFilter.selectedFilter === BehaviorFilters.completed ?
         analytics.filter(a => a.PercentViewed === 1) :
         analytics.filter(a => a.PercentViewed > 0 && a.PercentViewed < 1);

   const getFilteredAnalyticsBySelectedCollection = (): IMediaAnalyticsModel[] =>
      state.selectedCollection.MemberDetails.map((member: IMemberDetailsModel, index: number) =>
         analyticsData.find(data => data.TechSmithId === member.TechSmithId) ||
            {
               FirstName: '',
               LastName: '',
               DisplayName: member.DisplayName || state.selectedCollection.Members[index] || t('analytics.anonymousUser'),
               PercentViewed: 0,
               HasCompletedView: false,
               ViewCount: 0,
               TechSmithId: member.TechSmithId
            } as IMediaAnalyticsModel
      );

   const getAnalytics = async () => {
      analyticsData || updateState({isWorking: true, hasError: false});
      await updateAnalyticsDataFromServer(MediaId);
   };

   useEffect(() => {
      if (!config.featureSwitches.DisableAnalyticsAndQuizEditing) {
         void getAnalytics();
      }
   }, [/* This should remain empty because we only want this to trigger on initial load */]);

   useEffect(() => {
      analyticsData && updateState({isWorking: false, hasError: false});
   }, [analyticsData]);

   const onDownloadClick = async () => {
      updateState({isWorking: true, hasError: false});

      try {
         await quizApi.downloadViewingAnalyticsData(Hash);
         updateState({isWorking: false, hasError: false});
      } catch (e) {
         if (e?.status === Constants.statusCodes.notFound) {
            updateState({isWorking: false, hasError: true, customErrorMessage: t('analytics.errorAnalyticsNotFound')});
         } else {
            updateState({isWorking: false, hasError: true, customErrorMessage: null});
         }
      }
   };

   const setBehaviorFilter = (filter: BehaviorFilters) => {
      updateState({selectedBehaviorFilter: {selectedFilter: filter}});
   };

   const setCollectionFilter = (collection: ICollectionModel) => {
      updateState({selectedCollection: collection});
   };

   const filteredAnalyticsBySelectedCollection = state.selectedCollection ? getFilteredAnalyticsBySelectedCollection() : analyticsData || [];
   const filteredAnalyticsByCompletion = state.selectedBehaviorFilter.selectedFilter !== BehaviorFilters.all ? getFilteredAnalyticsByCompletion(filteredAnalyticsBySelectedCollection) : filteredAnalyticsBySelectedCollection;
   const showCollectionsDropDownLabel = state.selectedCollection ?
      t('analytics.showCollectionsDropDown', {collection: state.selectedCollection.Name}) :
      t('analytics.showCollectionsDropDown', {collection: t('analytics.allViewers')});

   return (
      <TabContentWrapper
         showSpinner={state.isWorking}
         showError={state.hasError}
         customErrorMessage={state.customErrorMessage}
      >
         <AnalyticsHeaderWrapper>
            <AnalyticsHeader theme={theme}>
               {t('analytics.segmentHeader')}
            </AnalyticsHeader>
            <PopoverButton
               buttonGlyph={<Glyphs.CaratDown16x16 />}
               glyphPlacement={TscButtonGlyphPlacement.right}
               origin={TscPopoverOrigin.bottomLeft}
               popoverMenu={<CollectionsMenu setFilter={setCollectionFilter} />}
               themeName={theme}
               showArrow={false}
               closeMenuOnClick={true}
               buttonTitle={showCollectionsDropDownLabel}
               buttonText={showCollectionsDropDownLabel}
               testId="analytics-collection-drop-down"
               buttonType={TscButtonType.secondary}
               disabled={sidebarCollections.filter(collection => CollectionIds.find(hash => hash === collection.Id)).length === 0}
               style={{display: 'flex'}}
               useMobileMenu={true}
               popoverMenuMaxWidth="30rem"
               popoverMenuMaxHeight="none"
               mobileContextMenuZindex={cssConstants.zIndex.mobileContextMenu}
               mobileContextMenuTitle={t('analytics.segmentHeader')}
            />
         </AnalyticsHeaderWrapper>
         <AnalyticsSegmentData analytics={filteredAnalyticsBySelectedCollection} />
         <UserBehaviorHeaderWrapper theme={theme}>
            <AnalyticsHeader theme={theme}>
               {t('analytics.behaviorHeader')}
            </AnalyticsHeader>
            <HeaderRightSection>
               <PopoverButton
                  buttonGlyph={<Glyphs.CaratDown16x16 />}
                  glyphPlacement={TscButtonGlyphPlacement.right}
                  origin={TscPopoverOrigin.bottom}
                  popoverMenu={<BehaviorFilterMenu setFilter={setBehaviorFilter} />}
                  themeName={theme}
                  showArrow={false}
                  closeMenuOnClick={true}
                  buttonTitle={t('analytics.showCollectionsDropDown', {collection: state.selectedBehaviorFilter.selectedFilter})}
                  buttonText={t('analytics.showCollectionsDropDown', {collection: state.selectedBehaviorFilter.selectedFilter})}
                  testId="analytics-completion-filter-drop-down"
                  buttonType={TscButtonType.tertiary}
                  disabled={!analyticsData?.length}
               />
               <ControlButton
                  label={t('analytics.download')}
                  buttonType={TscButtonType.tertiary}
                  glyph={<Glyphs.Download16x16 />}
                  disabled={ViewCount === 0}
                  themeName={theme}
                  onClick={onDownloadClick}
                  testId="reset-data-modal-download-button"
               />
            </HeaderRightSection>
         </UserBehaviorHeaderWrapper>
         <UserBehaviorData
            analytics={filteredAnalyticsByCompletion}
            selectedCollection={state.selectedCollection}
         />
      </TabContentWrapper>
   );
};

export interface IAnalyticsTabState extends IWorkerState {
   selectedCollection: ICollectionModel;
   selectedBehaviorFilter: IBehaviorFilterEnum;
}

export interface IStateMappedProps {
   analyticsData: IMediaAnalyticsModel[];
   sidebarCollections: ICollectionModel[];
   CollectionIds: string[];
   MediaId: string;
   Hash: string;
   ViewCount: number;
}

const mapStatesToProps = (
   {isWorking, hasError, customErrorMessage}: IWorkerState,
   {analyticsData}: Partial<IStateMappedProps>,
   {sidebarCollections}: Partial<IStateMappedProps>,
   {CollectionIds, MediaId, Hash, ViewCount}: Partial<IStateMappedProps>
): IWorkerState & IStateMappedProps => ({isWorking, hasError, customErrorMessage, analyticsData, sidebarCollections, CollectionIds, MediaId, Hash, ViewCount});

export default withMemoizedContexts(mapStatesToProps, analyticsDataStore, collectionsStore, mediaStore)(AnalyticsTabBase);
